import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { MagicReportSection } from './MagicReportSection';
import { CommentsSection } from './CommentsSection';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import {
  STORED_MATERIALS_G703_NO_REQUEST_TEXT,
  STORED_MATERIALS_G703_YES_OFFSITE_TEXT,
  STORED_MATERIALS_G703_YES_ONSITE_TEXT,
  STORED_MATERIALS_G703_YES_MIXED_TEXT,
  STORED_MATERIALS_OTHER_MATERIALS_INTRO_TEXT,
  STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT,
  STORED_MATERIALS_SECTION_CONFIG,
} from './sections.config';
import { DrawContext, SectionFieldCollection, SectionFieldDTO, FieldType } from '../magic-report.type';
import { getCommentsField } from './section-utils';
import { StoredMaterialsTable, StoredMaterialRow } from './StoredMaterialsTable';
import { useMagicReport } from '../context/MagicReportContext';

const ROWS_FIELD = 'StoredMaterialsRows';
const G703_INTRO_FIELD = 'G703MaterialsIntroComment';
const G703_COMMENT_FIELD = 'G703MaterialsComment';
const OTHER_INTRO_FIELD = 'OtherMaterialsIntro';
const OTHER_LIST_FIELD = 'OtherMaterialsList';

const listDescriptions = (rows: StoredMaterialRow[]): string => {
  if (rows.length === 0) return '';
  if (rows.length === 1) return rows[0].description;
  const descs = rows.map(r => r.description);
  return descs.slice(0, -1).join(', ') + ' and ' + descs[descs.length - 1];
};

const generateSingleItemComment = (row: StoredMaterialRow): string => {
  if (row.storedOffsite !== 'Yes') return '';

  const desc = row.description || 'stored';
  const visited = row.visitedOffsite === 'Yes';
  const insured = row.insuranceCert === 'Yes';
  const invoiced = row.invoicePresent === 'Yes';
  const photos = row.photosReceived === 'Yes';

  const parts: string[] = [];

  // Sentence 1: always present
  parts.push(
    visited
      ? `At the request of the lender, we visited the site where the ${desc} materials were located.`
      : `At the request of the lender, we did not visit the site where the ${desc} materials were located.`,
  );

  // Sentence 2: only relevant when not visited. Photos received -> "however" sentence;
  // no photos either -> a distinct callout instead (they're mutually exclusive).
  if (!visited && photos) {
    parts.push(`However, we were provided with photos for these materials that were consistent with the request.`);
  } else if (!visited && !photos) {
    parts.push(`We also have not received photos of the ${desc} materials from the Borrower or Contractor.`);
  }

  // Sentence 3: documentation status — always present, covers all four insurance×invoice combos
  if (!insured && !invoiced) {
    parts.push(
      `We were not provided with documentation stating that any were stored in the subcontractor or supplier's own insured warehouse or area, and Purchase Orders or Receipts were not received to substantiate any of the current request for funding amount.`,
    );
  } else if (insured && invoiced) {
    parts.push(
      `We were provided with documentation stating that they were stored in the subcontractor or supplier's own insured warehouse or area, and Purchase Orders or Receipts substantiated the current request for funding amount.`,
    );
  } else if (!insured && invoiced) {
    parts.push(
      `We were not provided with documentation stating that any were stored in the subcontractor or supplier's own insured warehouse or area, but Purchase Orders or Receipts substantiated the current request for funding amount.`,
    );
  } else {
    // insured && !invoiced
    parts.push(
      `We were provided with documentation stating that they were stored in the subcontractor or supplier's own insured warehouse or area, but Purchase Orders or Receipts were not received to substantiate any of the current request for funding amount.`,
    );
  }

  // Sentence 4: omitted only when visited=yes, insured=yes, invoiced=yes
  if (!(visited && insured && invoiced)) {
    const missing: string[] = [];
    if (!invoiced) missing.push('invoice backup');
    if (!insured) missing.push('evidence of insurance');
    if (!visited)
      missing.push(!photos ? 'physically observing the materials, nor photos of the same' : 'physically observing the materials');
    if (missing.length > 0) {
      const missingText = missing.length === 1 ? missing[0] : missing.slice(0, -1).join(', ') + ' or ' + missing[missing.length - 1];
      parts.push(`We leave the funding of the Off-Site Stored Materials without ${missingText} to the discretion of the lender.`);
    }
  }

  return parts.join(' ');
};

// Accepts only offsite rows. Delegates to generateSingleItemComment when there is exactly one.
const generateMultiItemComment = (offsiteRows: StoredMaterialRow[]): string => {
  if (!offsiteRows.length) return '';
  if (offsiteRows.length === 1) return generateSingleItemComment(offsiteRows[0]);

  const allCount = offsiteRows.length;
  const visitedRows = offsiteRows.filter(r => r.visitedOffsite === 'Yes');
  const notVisitedRows = offsiteRows.filter(r => r.visitedOffsite === 'No');

  const parts: string[] = [];

  // Sentence 1: visit statement
  if (visitedRows.length === allCount) {
    parts.push(`At the request of the lender, we visited the sites where all the materials were located.`);
  } else if (visitedRows.length === 0) {
    parts.push(`At the request of the lender, we did not visit the sites where any of the materials were located.`);
  } else {
    parts.push(
      `At the request of the lender, we visited the site where the ${listDescriptions(visitedRows)} materials were located but no others.`,
    );
  }

  // Sentence 2: photos — only relevant for non-visited items. The "no photos" callout (when
  // present) comes first, followed by the "however, photos received" sentence (when present).
  const photosRows = notVisitedRows.filter(r => r.photosReceived === 'Yes');
  const noPhotosRows = notVisitedRows.filter(r => r.photosReceived === 'No');
  if (noPhotosRows.length > 0) {
    if (noPhotosRows.length === notVisitedRows.length) {
      parts.push(`We also have not received photos of the materials from the Borrower or Contractor.`);
    } else {
      parts.push(`We also have not received photos of the ${listDescriptions(noPhotosRows)} materials from the Borrower or Contractor.`);
    }
  }
  if (photosRows.length > 0) {
    if (photosRows.length === notVisitedRows.length) {
      parts.push(`However, we were provided with photos for all of these materials that were consistent with the request.`);
    } else {
      parts.push(
        `However, we were provided with photos for the following materials that were consistent with the request: ${listDescriptions(photosRows)}.`,
      );
    }
  }

  // Sentence 3: documentation (insurance + invoice) with all/any optimisation
  const insuredRows = offsiteRows.filter(r => r.insuranceCert === 'Yes');
  const invoicedRows = offsiteRows.filter(r => r.invoicePresent === 'Yes');
  const allInsured = insuredRows.length === allCount;
  const noneInsured = insuredRows.length === 0;
  const allInvoiced = invoicedRows.length === allCount;
  const noneInvoiced = invoicedRows.length === 0;

  if (noneInsured && noneInvoiced) {
    parts.push(
      `We were not provided with documentation stating that any were stored in the subcontractor or supplier's own insured warehouse or area, and Purchase Orders or Receipts were not received to substantiate any of the current request for funding amount.`,
    );
  } else if (allInsured && allInvoiced) {
    parts.push(
      `We were provided with documentation stating that all were stored in the subcontractor or supplier's own insured warehouse or area, and Purchase Orders or Receipts substantiated the current request for funding amount.`,
    );
  } else if (noneInsured && allInvoiced) {
    parts.push(
      `We were not provided with documentation stating that any were stored in the subcontractor or supplier's own insured warehouse or area, but Purchase Orders or Receipts substantiated the current request for funding amount for all materials.`,
    );
  } else if (allInsured && noneInvoiced) {
    parts.push(
      `We were provided with documentation stating that all were stored in the subcontractor or supplier's own insured warehouse or area, but Purchase Orders or Receipts were not received to substantiate any of the current request for funding amount.`,
    );
  } else {
    // Mixed: mention positives by description list; sentence 4 covers the negatives
    const insuredList = insuredRows.length ? listDescriptions(insuredRows) : null;
    const invoicedList = invoicedRows.length ? listDescriptions(invoicedRows) : null;
    if (insuredList && invoicedList) {
      parts.push(
        `We were provided with documentation for the ${insuredList} materials stating that they were stored in the subcontractor or supplier's own insured warehouse or area, and Purchase Orders or Receipts substantiated the current request for funding amount for the ${invoicedList} materials.`,
      );
    } else if (insuredList) {
      parts.push(
        `We were provided with documentation for the ${insuredList} materials stating that they were stored in the subcontractor or supplier's own insured warehouse or area.`,
      );
    } else {
      parts.push(`Purchase Orders or Receipts substantiated the current request for funding amount for the ${invoicedList} materials.`);
    }
  }

  // Sentence 4: discretion — omitted only when all items have invoice, insurance, and were visited
  const noInvoiceRows = offsiteRows.filter(r => r.invoicePresent === 'No');
  const noInsuranceRows = offsiteRows.filter(r => r.insuranceCert === 'No');
  const allCovered = noInvoiceRows.length === 0 && noInsuranceRows.length === 0 && notVisitedRows.length === 0;

  if (!allCovered) {
    const missingClauses: string[] = [];

    if (noInvoiceRows.length > 0) {
      missingClauses.push(
        noInvoiceRows.length === allCount ? 'invoice backup' : `invoice backup for the ${listDescriptions(noInvoiceRows)} materials`,
      );
    }
    if (noInsuranceRows.length > 0) {
      missingClauses.push(
        noInsuranceRows.length === allCount
          ? 'evidence of insurance'
          : `evidence of insurance for the ${listDescriptions(noInsuranceRows)} materials`,
      );
    }
    if (notVisitedRows.length > 0) {
      const allNotVisitedLackPhotos = noPhotosRows.length === notVisitedRows.length;
      if (allNotVisitedLackPhotos) {
        missingClauses.push(
          notVisitedRows.length === allCount
            ? 'physically observing the materials, nor photos of the same'
            : `physically observing the ${listDescriptions(notVisitedRows)} materials, nor photos of the same`,
        );
      } else {
        missingClauses.push(
          notVisitedRows.length === allCount
            ? 'physically observing the materials'
            : `physically observing the ${listDescriptions(notVisitedRows)} materials`,
        );
        if (noPhotosRows.length > 0) {
          missingClauses.push(`photos of the ${listDescriptions(noPhotosRows)} materials`);
        }
      }
    }

    if (missingClauses.length > 0) {
      const finalClause =
        missingClauses.length === 1
          ? missingClauses[0]
          : missingClauses.slice(0, -1).join(', ') + ' or ' + missingClauses[missingClauses.length - 1];
      parts.push(`We leave the funding of the Off-Site Stored Materials without ${finalClause} to the discretion of the lender.`);
    }
  }

  return parts.join(' ');
};

/** The first-sentence intro (e.g. "The Contractor has made a specific request..."), rendered above the table. */
export const generateG703IntroText = (rows: StoredMaterialRow[]): string => {
  if (!rows.length) {
    return STORED_MATERIALS_G703_NO_REQUEST_TEXT;
  }

  const hasOffsite = rows.some(r => r.storedOffsite === 'Yes');
  const hasOnsite = rows.some(r => r.storedOffsite !== 'Yes');

  if (hasOffsite && hasOnsite) return STORED_MATERIALS_G703_YES_MIXED_TEXT;
  if (hasOffsite) return STORED_MATERIALS_G703_YES_OFFSITE_TEXT;
  return STORED_MATERIALS_G703_YES_ONSITE_TEXT;
};

/** The offsite narrative that follows the table, rendered in the G703 Comments box. */
export const generateG703DetailText = (rows: StoredMaterialRow[]): string => {
  const offsite = rows.filter(r => r.storedOffsite === 'Yes');
  if (!offsite.length) return '';
  return offsite.length === 1 ? generateSingleItemComment(offsite[0]) : generateMultiItemComment(offsite);
};

/** @deprecated kept for callers that still want the combined intro+detail text as one string */
export const generateG703Comment = (rows: StoredMaterialRow[]): string => {
  const intro = generateG703IntroText(rows);
  const detail = generateG703DetailText(rows);
  return detail ? `${intro}\n${detail}` : intro;
};

const StoredMaterialsSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();

  // Every covered payapp (G702). Reports opened before multi-draw have no list, so the single
  // primary-draw mirror becomes a one-payapp list and this renders as before (now labelled).
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        drawSummary: reportState.drawSummary,
        budgetSummary: reportState.budgetSummary,
        lineItems: reportState.lineItems,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary, reportState.lineItems]);

  const firstDrawId = draws[0]?.drawId;
  // The comment spans every payapp's materials, so it works off all their line items together.
  const lineItems = useMemo(() => draws.flatMap(d => d.lineItems || []), [draws]);
  const syncFnsRef = React.useRef<Map<string, () => Promise<void>>>(new Map());

  const handleFieldChangeRef = React.useRef(handleFieldChange);
  handleFieldChangeRef.current = handleFieldChange;
  const getFieldRef = React.useRef(getField);
  getFieldRef.current = getField;

  const rowsField = getField(ROWS_FIELD);

  /** Every payapp's rows, with pre-multi-draw rows bound to the first payapp. */
  const rows: StoredMaterialRow[] = useMemo(() => {
    return (
      rowsField?.entries
        ?.map(entry => {
          try {
            return JSON.parse(entry.value || '') as StoredMaterialRow;
          } catch {
            return null;
          }
        })
        .filter((r): r is StoredMaterialRow => r !== null)
        .map(r => (r.drawId ? r : { ...r, drawId: firstDrawId })) || []
    );
  }, [rowsField, firstDrawId]);

  const rowsByDraw = useMemo(() => {
    const grouped = new Map<string, StoredMaterialRow[]>();
    draws.forEach(d => grouped.set(d.drawId, []));
    rows.forEach(row => {
      const key = row.drawId || firstDrawId;
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    });
    return grouped;
  }, [rows, draws, firstDrawId]);

  /**
   * Authoritative row list for merging writes.
   *
   * Every payapp's table writes to the same `StoredMaterialsRows` field and several are mounted at
   * once, so merging against a render-time snapshot would let two writes in the same tick clobber
   * each other. This ref is updated synchronously at the merge point below.
   */
  const allRowsRef = React.useRef<StoredMaterialRow[]>(rows);
  allRowsRef.current = rows;

  /**
   * A `handleFieldChange` scoped to one payapp's table: writes to the shared rows field replace only
   * that payapp's rows. Anything else passes through, so StoredMaterialsTable needs no changes.
   */
  const makeDrawScopedFieldChange = React.useCallback(
    (drawId: string) => (fieldName: string, updates: Partial<SectionFieldDTO>) => {
      if (fieldName !== ROWS_FIELD || !updates.entries) {
        handleFieldChange(fieldName, updates);
        return;
      }
      const mine = updates.entries
        .map((entry): StoredMaterialRow | null => {
          try {
            return { ...(JSON.parse(entry.value || '') as StoredMaterialRow), drawId };
          } catch {
            return null;
          }
        })
        .filter((r): r is StoredMaterialRow => r !== null);
      const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
      const next = [...others, ...mine];
      allRowsRef.current = next;
      handleFieldChange(ROWS_FIELD, {
        ...updates,
        entries: next.map((row, index) => ({ value: JSON.stringify(row), order: index })),
      });
    },
    [firstDrawId, handleFieldChange],
  );

  const commentsField = getCommentsField(data, {
    isCommentField: (name, type) => type === FieldType.TextArray && name.toLowerCase().includes('comment'),
  });

  // Tracks the row signature these fields were auto-populated for, so a genuine table change
  // (not a user clearing the text) is what allows auto-fill to run again.
  const lastRowsSignatureRef = React.useRef<string | null>(null);
  const wasCommentManuallyClearedRef = React.useRef(false);
  const wasIntroManuallyClearedRef = React.useRef(false);
  const wasOtherListManuallyClearedRef = React.useRef(false);
  const lastG703IntroSignatureRef = React.useRef<string | null>(null);

  useEffect(() => {
    const rowsSignature = JSON.stringify(rows);
    if (lastRowsSignatureRef.current !== null && lastRowsSignatureRef.current !== rowsSignature) {
      wasCommentManuallyClearedRef.current = false;
      wasIntroManuallyClearedRef.current = false;
      wasOtherListManuallyClearedRef.current = false;
    }
    lastRowsSignatureRef.current = rowsSignature;

    // Only auto-populate when a field is empty AND it hasn't been deliberately cleared since the
    // rows last changed - otherwise clearing it gets immediately overwritten. Explicit actions
    // (onRowsSynced, refresh buttons, "Run this month") bypass this and always overwrite.
    const currentComment = (getFieldRef.current(G703_COMMENT_FIELD)?.value || '').trim();
    if (!currentComment) {
      wasCommentManuallyClearedRef.current = true;
    }
    const autoComment = generateG703DetailText(rows);
    if (!wasCommentManuallyClearedRef.current && !currentComment && autoComment) {
      handleFieldChangeRef.current(G703_COMMENT_FIELD, { value: autoComment });
    }

    const autoG703Intro = generateG703IntroText(rows);
    if (lastG703IntroSignatureRef.current !== autoG703Intro) {
      lastG703IntroSignatureRef.current = autoG703Intro;
      const currentG703Intro = (getFieldRef.current(G703_INTRO_FIELD)?.value || '').trim();
      if (currentG703Intro !== autoG703Intro) {
        handleFieldChangeRef.current(G703_INTRO_FIELD, { value: autoG703Intro });
      }
    }

    const introValue = getFieldRef.current(OTHER_INTRO_FIELD)?.value;
    if (!introValue) {
      wasIntroManuallyClearedRef.current = true;
    }
    if (!wasIntroManuallyClearedRef.current && !introValue) {
      handleFieldChangeRef.current(OTHER_INTRO_FIELD, { value: STORED_MATERIALS_OTHER_MATERIALS_INTRO_TEXT });
    }

    const otherListValue = getFieldRef.current(OTHER_LIST_FIELD)?.value;
    if (!wasOtherListManuallyClearedRef.current && !otherListValue) {
      handleFieldChangeRef.current(OTHER_LIST_FIELD, { value: STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT });
    } else if (!otherListValue) {
      wasOtherListManuallyClearedRef.current = true;
    }
  }, [rows]);

  const g703CommentField = getField(G703_COMMENT_FIELD);

  // Register/unregister sync function — runs every payapp's table sync.
  useEffect(() => {
    registerSectionSync('storedMaterials', async () => {
      await Promise.all(Array.from(syncFnsRef.current.values()).map(fn => fn()));
      // "Run this month" should also restore the boilerplate text for these two fields,
      // same as clicking their individual refresh buttons.
      handleFieldChange(G703_INTRO_FIELD, { value: generateG703IntroText(rows) });
      handleFieldChange(OTHER_LIST_FIELD, { value: STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT });
    });
    return () => {
      unregisterSectionSync('storedMaterials');
    };
  }, [registerSectionSync, unregisterSectionSync, handleFieldChange, rows]);

  const layout = useMemo(
    () => ({
      singleColumn: [
        {
          fieldName: G703_INTRO_FIELD,
          type: 'text-with-refresh' as const,
          label: 'G703 Materials Intro',
          onRefresh: () => handleFieldChange(G703_INTRO_FIELD, { value: generateG703IntroText(rows) }),
        },
        // One labelled table per covered payapp. Labelled even for a single payapp, matching the
        // report's "<G702 Name> G703 Listed Materials:" heading.
        ...draws.map((draw, drawIndex) => {
          const drawId = draw.drawId;
          const drawRows = rowsByDraw.get(drawId) || [];
          const scopedRowsField: SectionFieldDTO | undefined = rowsField
            ? { ...rowsField, entries: drawRows.map((row, index) => ({ value: JSON.stringify(row), order: index })) }
            : undefined;
          const label = draw.drawSummary?.name || `Draw ${drawIndex + 1}`;

          return {
            fieldName: `${ROWS_FIELD}__${drawId}`,
            type: 'custom-component' as const,
            label: '',
            renderComponent: () => (
              <Box sx={{ mt: drawIndex === 0 ? 0 : 3 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                  {label} G703 Listed Materials:
                </Typography>
                <StoredMaterialsTable
                  rowsField={scopedRowsField}
                  handleFieldChange={makeDrawScopedFieldChange(drawId)}
                  lineItems={draw.lineItems}
                  onRowsSynced={updated => {
                    // The comment covers every payapp, so regenerate from this payapp's synced rows
                    // plus every other payapp's current rows.
                    const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
                    const combined = [...others, ...updated.map(r => ({ ...r, drawId }))];
                    handleFieldChange(G703_COMMENT_FIELD, { value: generateG703DetailText(combined) });
                  }}
                  onSyncFunctionReady={syncFn => {
                    syncFnsRef.current.set(drawId, syncFn);
                  }}
                />
              </Box>
            ),
          };
        }),
        {
          fieldName: G703_COMMENT_FIELD,
          type: 'text' as const,
          label: 'G703 Comments',
        },
        {
          fieldName: OTHER_LIST_FIELD,
          type: 'text-with-refresh' as const,
          label: 'List Other Material Comments',
          defaultValue: STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT,
          onRefresh: () => handleFieldChange(OTHER_LIST_FIELD, { value: STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT }),
        },
      ],
    }),
    [draws, rowsField, rowsByDraw, makeDrawScopedFieldChange, firstDrawId, handleFieldChange, rows, getField],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange(commentsField.fieldName, updated)}
          onBook={onBook}
        />
      )}
    </Box>
  );
};

export const StoredMaterialsSection: React.FC<{ magicReportId: string }> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={STORED_MATERIALS_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <StoredMaterialsSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
